import type { Fetcher } from '@remix-run/react';
import { useFetcher } from '@remix-run/react';
import { useState } from 'react';
import { PAGE_POSITIONS } from 'types';
import type { TrackableProduct } from 'types/TrackableProduct';

import FlagIcon from '~/assets/icons/Flag';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';

import type { User } from '../../types/User';
import ClickSaveProductEvent from '../events/ClickSaveProductEvent';
import Modals from './Modals';

type ButtonStyle = 'compact' | 'normal';

type Props = {
  buttonStyle: ButtonStyle;
  product: TrackableProduct;
};

function getImage(product: TrackableProduct) {
  return product.image;
}

function getPagePosition(buttonStyle: ButtonStyle) {
  return buttonStyle === 'compact'
    ? PAGE_POSITIONS.productCard
    : PAGE_POSITIONS.body;
}

enum FetchState {
  Idle,
  Setting,
  Unsetting
}

function getFetchState(fetcher: Fetcher) {
  if (fetcher.state === 'idle') return FetchState.Idle;

  return fetcher?.formMethod === 'PUT'
    ? FetchState.Setting
    : FetchState.Unsetting;
}

function getIsSaved(state: FetchState, product: Props['product'], user: User) {
  if (user.isLoggedIn && state !== FetchState.Idle)
    return state === FetchState.Setting;

  return Boolean(
    user.actions?.likes?.products?.includes(product.legacy_id || '')
  );
}

const SaveProductButton = ({ product, buttonStyle }: Props) => {
  const user = useUser();
  const fetcher = useFetcher();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { isBot } = useRootData();

  const fetchState = getFetchState(fetcher);
  const isSaved = getIsSaved(fetchState, product, user);

  function onClick() {
    new ClickSaveProductEvent({
      buttonName: 'SAVE_PRODUCT',
      isBot,
      pagePosition: getPagePosition(buttonStyle),
      product,
      user
    }).send();

    if (!isSaved) gtm.Ecommerce.addToWishlist(product);

    if (!user.isLoggedIn) return setShowLoginModal(true);

    fetcher.submit('', {
      action: `/bolt/save-product/${product.legacy_id}`,
      method: isSaved ? 'delete' : 'put'
    });
  }

  return (
    <>
      {showLoginModal && (
        <Modals.Login
          modalType="save-product"
          onDismiss={() => setShowLoginModal(false)}
          image={getImage(product)}
          postAuthOptions={{
            action: 'like',
            legacyProductId: product.legacy_id
          }}
        />
      )}
      <Button
        onClick={onClick}
        disabled={fetchState !== FetchState.Idle}
        isSaved={isSaved}
        buttonStyle={buttonStyle}
      />
    </>
  );
};

const Button = ({
  buttonStyle,
  isSaved,
  disabled,
  onClick
}: {
  buttonStyle: ButtonStyle;
  disabled: boolean;
  isSaved: Boolean;
  onClick: () => void;
}) => {
  if (buttonStyle === 'compact') {
    return (
      <button
        className="transition-transform duration-200 ease-in-out hover:scale-125"
        onClick={onClick}
        disabled={disabled}
        type="submit"
        data-cy="save-product-button-compact"
      >
        <FlagIcon
          width="16"
          height="16"
          fill={isSaved ? 'black' : 'white'}
          stroke="black"
          className="mx-1"
        />
      </button>
    );
  }

  return (
    <button
      data-cy="save-product-button-normal"
      className={`flex items-center gap-1 rounded-lg border border-black p-2 text-xs ${
        isSaved ? 'bg-white text-black' : 'bg-black text-white'
      }`}
      disabled={disabled}
      onClick={onClick}
      type="submit"
    >
      <FlagIcon
        width="16"
        height="16"
        fill={isSaved ? 'black' : 'none'}
        stroke={isSaved ? 'black' : 'white'}
        className="mx-1"
      />
      {isSaved ? 'Item saved' : 'Save item'}
    </button>
  );
};

export default SaveProductButton;
